/*
 * One reckoning places every list logging offers — `/specs/features/food-order.spec.md`.
 *
 * The log is read for its habits — the thing that opens the day, the habit of this part of it,
 * the rest of a plate just logged, what follows a thing an hour or so later, what comes after
 * training — and each reading says how sure it is. Where no reading has anything to say of a
 * thing, the reckoning falls back on what the thing is worth on its own. So a list leads with
 * what the log expects, follows with what is familiar, and ends with what has hardly been
 * touched.
 *
 * Every app works this out for itself, off what it holds, with no server to ask — so every
 * number here is the spec's, and the Android port in `data/FoodSuggestions.kt` runs the same
 * test cases.
 */

import {
  LOGS_READ,
  RECENT_DAYS,
  partOfMoment,
  partOfTheDay,
  rounded,
  worthOf,
  type Candidate,
  type PartOfTheDay,
} from './order'

/** How many days of log a reading is made off. The day asked for is not one of them. */
export const WINDOW_DAYS = 28

/**
 * What share of the days it is logged on a thing has to have had today before it has had its
 * day.
 */
export const SPENT_SHARE = 0.85

/** How many occasions a reading rests on before its say is worth the whole of its share. */
export const LEAST_OCCASIONS = 8

/**
 * What a say has to reach to be worth saying at all. A thing done on fewer than one occasion in
 * four is not what comes next, and a list led by it would be led by noise standing over things
 * eaten every day.
 */
export const LEAST_SAY = 0.25

/** A habit missed this many occasions running is not a habit today, whatever its share. */
export const MISSED_IN_A_ROW = 2

/**
 * How far either side of the time asked for the habit of the hour reads, in minutes. The stretch
 * is centred on the asking rather than being one of the parts of the day, so a dinner that falls
 * either side of six is one habit and not two halves of one, and a bar at ten and a bar at two
 * are two habits rather than one that is already there.
 */
export const HABIT_STRETCH_MINUTES = 180

/** How far either side of one of a thing's usual times a time still counts as usual, in minutes. */
export const TIMES_SLACK_MINUTES = 30

/** The tenth of times set aside at each end: the count of times divided by this, rounded down. */
export const TIMES_SET_ASIDE = 10

/**
 * How far either side of an entry's time the rest of its plate lies. Things logged one by one
 * land a few minutes apart, so a sitting is read as a stretch rather than as one minute.
 */
export const PLATE_MINUTES = 15

/** How long a plate stands as a plate, counting from the moment its entry was logged. */
export const COMPANION_STANDS_MINUTES = 90

/** How long after a thing a follower follows it — from, and to. */
export const FOLLOW_FROM_MINUTES = 30
export const FOLLOW_TO_MINUTES = 180

/** How long after a workout finishes the after-training reading holds. */
export const AFTER_TRAINING_MINUTES = 120

/** How many newest occurrences of a food all being one meal's makes it that meal. */
export const COLLAPSE_LOGS = 3

/**
 * How far apart two entries of one meal can have been logged and still be one press of it. The
 * times they were logged onto say nothing about it: a meal moved to another hour afterwards was
 * still logged in one press, and is one occurrence of the meal.
 */
export const ONE_PRESS_MS = 60_000

/**
 * When the day turns over for the clock: a time before this is counted twenty-four hours later,
 * so the night is one stretch and a snack at half past midnight stands after one at eleven.
 */
export const DAY_TURNS_MINUTES = 5 * 60

const MINUTES_IN_A_DAY = 24 * 60
const MS_IN_A_MINUTE = 60_000
const MS_IN_A_DAY = 86_400_000

/** The five ways the log is read for a habit. */
export type Reading = 'opener' | 'habit' | 'companion' | 'follower' | 'after-training'

/**
 * The order two readings are held apart in where they say the same of a thing over the same
 * occasions: the order the spec names them in.
 */
const READINGS_IN_ORDER: readonly Reading[] = [
  'opener',
  'habit',
  'companion',
  'follower',
  'after-training',
]

/** What a list is placed for: logging onto a day, or a form holding what it holds. */
export type Purpose = { kind: 'log' } | { kind: 'form'; plateKeys: readonly string[] }

/** What a list is placed for — a day, a time on it, the moment of asking, and a purpose. */
export interface Asking {
  /** The day being logged onto, as YYYY-MM-DD. */
  readonly day: string
  /** The time on that day the asking is for, as HH:MM. */
  readonly time: string
  /** The moment of asking: recency and the companion's ninety minutes count from it. */
  readonly now: number
  readonly purpose: Purpose
}

/** One entry as the reckoning reads it. */
export interface Logged {
  /** The food's key — its own id, or a library food's code; null for a quick entry. */
  readonly key: string | null
  /** What it is called, for the name tie-break and for the reason line. */
  readonly name: string
  /** The day it was logged onto, as YYYY-MM-DD. */
  readonly day: string
  /** The time it was logged onto, as HH:MM. */
  readonly time: string
  /** The moment it was logged, which is not the moment it was logged onto. */
  readonly loggedAt: number
  /** The meal it was logged from, where it was logged from a kept meal. */
  readonly mealId: string | null
  /**
   * Whether it was logged from a meal at all — a kept one, or something logged without being
   * kept, which names no meal. The worth leaves every such entry out of its food's times.
   */
  readonly fromMeal?: boolean
}

/** A workout the account has, shown on the food log or not. Only finished ones are passed. */
export interface TrainedWorkout {
  readonly finishedAt: number
}

/** A meal the account keeps: only a kept meal's logs are read as the meal. */
export interface SuggestibleMeal {
  readonly id: string
}

/** A meal as a row of a list: what `placed` is told about one. */
export type MealStanding = Extract<Standing, { kind: 'meal' }>

/** What a row of a list is, as the reckoning needs to know it. */
export type Standing =
  | {
      readonly kind: 'food'
      readonly key: string
      readonly name: string
      /** When the row was written, for a thing never logged. A library food says nothing. */
      readonly madeAt: number | null
    }
  | {
      readonly kind: 'meal'
      readonly id: string
      readonly name: string
      readonly madeAt: number | null
      /** How many times it has been logged in all. */
      readonly logCount: number
      /** The logs it carries; the newest five of them are read for the part of the day. */
      readonly logs: readonly { readonly loggedAt: number }[]
      readonly lastLoggedAt: number | null
    }
  | {
      readonly kind: 'unkept'
      readonly id: string
      readonly name: string
      /** Its one time, which is also its moment. */
      readonly loggedAt: number
    }

/** Everything the readings and the worth are made off. */
export interface PlacingInput<E> {
  /**
   * The entries of the ninety days the worth reads and of the day asked for; the readings take
   * the twenty-eight of them that are the window. More may be passed; the rest is left alone.
   */
  readonly entries: readonly E[]
  readonly of: (entry: E) => Logged
  readonly incompleteDays: readonly string[]
  readonly workouts: readonly TrainedWorkout[]
  readonly meals: readonly SuggestibleMeal[]
  readonly asking: Asking
}

/** One row of a list, placed. */
export interface Placed<T> {
  readonly row: T
  /** How likely it is to be the next thing logged, rounded to six places; nought where no reading spoke. */
  readonly estimate: number
  /** The reading whose say the estimate is, and null where none spoke. */
  readonly reading: Reading | null
  /** That reading's raw share, before it was scaled by the occasions it rests on. */
  readonly share: number | null
  readonly occasions: number | null
  /** What it is worth on its own, rounded to six places — what places it after the estimates. */
  readonly worth: number
  /**
   * Whether it has had its day: logged onto the day asked for as many times as it is logged on
   * nearly every day it is logged on at all. No reading speaks of it, and it stands after
   * everything that is not spent.
   */
  readonly spent: boolean
  /**
   * `meal:<id>` where a food row stood as a meal row of the list, which drops it: a row with
   * this set is never in the placed list, and it is here to say why the row went.
   */
  readonly standsAs: string | null
}

/**
 * A time of day in minutes from midnight, with a time before the day turns counted twenty-four
 * hours later — so half past midnight is 24:30 and stands after eleven at night.
 */
export function minutesOfClock(time: string): number {
  const [hour, minute] = time.split(':')
  const at = Number(hour) * 60 + Number(minute)
  return at < DAY_TURNS_MINUTES ? at + MINUTES_IN_A_DAY : at
}

/** Whether two counted times stand on one plate: a quarter of an hour apart at the most. */
function onOnePlate(at: number, beside: number): boolean {
  return Math.abs(at - beside) <= PLATE_MINUTES
}

/** The part of the day a counted time falls in, the night's late hours included. */
function partOfMinutes(at: number): PartOfTheDay {
  const within = ((at % MINUTES_IN_A_DAY) + MINUTES_IN_A_DAY) % MINUTES_IN_A_DAY
  return partOfTheDay(Math.floor(within / 60), within % 60)
}

/**
 * Whether a time is within when a thing tends to be logged in one reading's situation: the times
 * that count as kept, with the earliest tenth and the latest tenth set aside, and a time within
 * half an hour of one of those left. It is the times themselves and not the span they lie in, so
 * a thing logged at eight and again at noon is not expected at ten. With fewer than ten times
 * none is set aside, so a habit of a fortnight is not read off its two oddest days.
 */
export function withinUsualTimes(times: readonly number[], at: number): boolean {
  if (times.length === 0) {
    return false
  }
  const sorted = [...times].sort((a, b) => a - b)
  const aside = Math.floor(sorted.length / TIMES_SET_ASIDE)
  const left = sorted.slice(aside, sorted.length - aside)
  return left.some((time) => Math.abs(at - time) <= TIMES_SLACK_MINUTES)
}

/** The days of the window: the twenty-eight before the day asked for, oldest first. */
export function windowDays(day: string): string[] {
  const days: string[] = []
  for (let back = WINDOW_DAYS; back >= 1; back -= 1) {
    days.push(dayBefore(day, back))
  }
  return days
}

/**
 * The counted days: the days of the window that hold an entry — a quick entry counts — and are
 * not marked incomplete. Handed the days that hold one, with repeats and strays and all.
 */
export function countedDaysOf(
  loggedDays: readonly string[],
  day: string,
  incompleteDays: readonly string[],
): string[] {
  const inWindow = new Set(windowDays(day))
  const incomplete = new Set(incompleteDays)
  const counted = new Set<string>()
  for (const each of loggedDays) {
    if (inWindow.has(each) && !incomplete.has(each)) {
      counted.add(each)
    }
  }
  return [...counted].sort()
}

/** One time a thing was logged, as the readings ask after it. */
interface Occurrence {
  readonly thingId: string
  readonly day: string
  /** The time it was logged onto, counted. */
  readonly minutes: number
  loggedAt: number
  /** The meal it came from, where it did. */
  readonly mealId: string | null
}

/**
 * One occasion a reading held on: the things kept on it, each with the times that count as its
 * own for reading usual times off.
 */
interface Occasion {
  /** The greater the later; only the order matters. */
  readonly rank: number
  readonly kept: Map<string, number[]>
}

/** One reading in one situation. */
interface Situation {
  readonly reading: Reading
  readonly occasions: Occasion[]
  readonly alreadyThere: (thingId: string) => boolean
  /** Whether the reading's standing time is read off the thing's usual times. */
  readonly timed: boolean
}

/** What one reading says of one thing. */
interface Say {
  readonly reading: Reading
  readonly share: number
  readonly occasions: number
  readonly estimate: number
}

const foodThing = (key: string): string => `food:${key}`
const mealThing = (id: string): string => `meal:${id}`
const unkeptThing = (id: string): string => `unkept:${id}`

function pad(at: number): string {
  return `${at}`.padStart(2, '0')
}

function dayBefore(day: string, back: number): string {
  const [year = 0, month = 1, date = 1] = day.split('-').map(Number)
  const when = new Date(Date.UTC(year, month - 1, date) - back * MS_IN_A_DAY)
  return `${when.getUTCFullYear()}-${pad(when.getUTCMonth() + 1)}-${pad(when.getUTCDate())}`
}

/** How many days lie between two of them, the first being the later. */
function daysBetween(later: string, earlier: string): number {
  const [ly = 0, lm = 1, ld = 1] = later.split('-').map(Number)
  const [ey = 0, em = 1, ed = 1] = earlier.split('-').map(Number)
  return Math.round((Date.UTC(ly, lm - 1, ld) - Date.UTC(ey, em - 1, ed)) / MS_IN_A_DAY)
}

/** The day a moment falls on, by the clock the device is set to. */
function dayOfMoment(at: number): string {
  const when = new Date(at)
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`
}

/** The time of day a moment falls at, by the clock the device is set to. */
function clockOfMoment(at: number): string {
  const when = new Date(at)
  return `${pad(when.getHours())}:${pad(when.getMinutes())}`
}

/** The counted time of day a moment falls at, by the clock the device is set to. */
function minutesOfMoment(at: number): number {
  return minutesOfClock(clockOfMoment(at))
}

/**
 * The reckoning's day a time on a day belongs to: the day before, for a time before the day
 * turns. The reckoning's day runs from five in the morning to five the next, so a snack logged
 * at half past midnight onto the next day is that night's snack, and asking at ten past midnight
 * asks for the evening that has not yet ended.
 */
function countedDayOf(day: string, time: string): string {
  return minutesOfClock(time) >= MINUTES_IN_A_DAY ? dayBefore(day, 1) : day
}

/** The reckoning's day a moment falls on, by the clock the device is set to. */
function countedDayOfMoment(at: number): string {
  return countedDayOf(dayOfMoment(at), clockOfMoment(at))
}

/**
 * Entries in the order they were logged, and those logged at the same moment in the order of
 * their keys, so that no answer — a plate's first food, a name, a tie — depends on the order an
 * app happens to hold them in.
 */
function byLogging(a: Logged, b: Logged): number {
  if (a.loggedAt !== b.loggedAt) {
    return a.loggedAt - b.loggedAt
  }
  const named = a.key ?? ''
  const beside = b.key ?? ''
  return named < beside ? -1 : named > beside ? 1 : 0
}

/**
 * A meal's entries on one day split into its presses: entries logged within a minute of the one
 * before stand in the same press, whatever times they were logged onto.
 */
function pressesOf(logs: readonly Logged[]): Logged[][] {
  const presses: Logged[][] = []
  let press: Logged[] = []
  for (const log of [...logs].sort(byLogging)) {
    const last = press[press.length - 1]
    if (last !== undefined && log.loggedAt - last.loggedAt > ONE_PRESS_MS) {
      presses.push(press)
      press = []
    }
    press.push(log)
  }
  if (press.length > 0) {
    presses.push(press)
  }
  return presses
}

/** The thing a row is, as the readings name things. */
function thingOf(standing: Standing): string {
  switch (standing.kind) {
    case 'food':
      return foodThing(standing.key)
    case 'meal':
      return mealThing(standing.id)
    default:
      return unkeptThing(standing.id)
  }
}

/** What the readings had to say, what has had its day, and what stands as a meal. */
interface Reckoning {
  readonly says: Map<string, Say>
  readonly spent: Set<string>
  readonly collapsedTo: (thingId: string) => string | null
}

/**
 * The log read for its habits: what each reading that is on for this asking says of each thing
 * it speaks of, and the greatest of those says.
 */
function reckon<E>(input: PlacingInput<E>): Reckoning {
  const { asking } = input
  /* Every day here is the reckoning's own, running from five in the morning to five the next. */
  const day = countedDayOf(asking.day, asking.time)
  const askedMinutes = minutesOfClock(asking.time)
  const form = asking.purpose.kind === 'form'

  const window = new Set(windowDays(day))
  const held = input.entries
    .map((row) => input.of(row))
    .map((log) => ({ ...log, day: countedDayOf(log.day, log.time) }))
    .filter((log) => log.day === day || window.has(log.day))
    .sort(byLogging)

  const counted = countedDaysOf(
    held.filter((log) => log.day !== day).map((log) => log.day),
    day,
    input.incompleteDays,
  )
  const countedDays = new Set(counted)
  const mealById = new Map(input.meals.map((meal) => [meal.id, meal]))

  /*
   * Every entry of a day, quick entries and all, because the day's earliest time is the earliest
   * anything was logged at and not the earliest of the things that could be spoken of.
   */
  const entriesOnDay = new Map<string, Logged[]>()
  for (const log of held) {
    const on = entriesOnDay.get(log.day) ?? []
    on.push(log)
    entriesOnDay.set(log.day, on)
  }

  const occurrences: Occurrence[] = []
  for (const log of held) {
    const { key } = log
    if (key === null) {
      continue
    }
    occurrences.push({
      thingId: foodThing(key),
      day: log.day,
      minutes: minutesOfClock(log.time),
      loggedAt: log.loggedAt,
      mealId: log.mealId,
    })
  }
  /*
   * The entries logged from one meal in one press — onto one day, at moments within a minute of
   * each other — stand together as one occurrence of the meal, and as one apiece of the foods
   * among them, which the loop above has counted. Whatever times they were logged onto: one of
   * them moved to another hour afterwards was still one press.
   */
  const mealDays = new Map<string, { mealId: string; day: string; logs: Logged[] }>()
  for (const log of held) {
    if (log.mealId === null || !mealById.has(log.mealId)) {
      continue
    }
    const at = [log.mealId, log.day].join('/')
    const before = mealDays.get(at)
    if (before === undefined) {
      mealDays.set(at, { mealId: log.mealId, day: log.day, logs: [log] })
    } else {
      before.logs.push(log)
    }
  }
  for (const { mealId, day: on, logs } of mealDays.values()) {
    for (const press of pressesOf(logs)) {
      occurrences.push(pressed(press, mealId, on))
    }
  }
  const byThing = new Map<string, Occurrence[]>()
  const onDay = new Map<string, Occurrence[]>()
  for (const each of occurrences) {
    const of = byThing.get(each.thingId) ?? []
    of.push(each)
    byThing.set(each.thingId, of)
    const on = onDay.get(each.day) ?? []
    on.push(each)
    onDay.set(each.day, on)
  }
  const onTheDay = onDay.get(day) ?? []
  const things = [...byThing.keys()]

  /*
   * What has had its day: logged onto the day asked for as many times as it is logged on nearly
   * every day it is logged on at all. Coffee once every morning is spent by its one log; a fruit
   * eaten twice most days is not spent by its first. Nothing is spent in a form.
   */
  const spent = new Set<string>()
  if (!form) {
    for (const [thingId, each] of byThing) {
      const counts = new Map<string, number>()
      for (const occurrence of each) {
        if (countedDays.has(occurrence.day)) {
          counts.set(occurrence.day, (counts.get(occurrence.day) ?? 0) + 1)
        }
      }
      if (counts.size < LEAST_OCCASIONS) {
        continue
      }
      const already = each.filter((occurrence) => occurrence.day === day).length
      const had = [...counts.values()].filter((count) => count <= already).length
      if (rounded(had / counts.size) >= SPENT_SHARE) {
        spent.add(thingId)
      }
    }
  }

  const timesOn = (thingId: string, on: string): number[] =>
    (onDay.get(on) ?? []).filter((each) => each.thingId === thingId).map((each) => each.minutes)

  const situations: Situation[] = []

  /* Every reading is on when logging asks; a form turns on the companion alone. */
  if (!form) {
    /* The opener: nothing has been logged onto the day, and the thing is what opens it. */
    if ((entriesOnDay.get(day)?.length ?? 0) === 0) {
      situations.push({
        reading: 'opener',
        occasions: counted.map((each, rank) => {
          const earliest = Math.min(
            ...(entriesOnDay.get(each) ?? []).map((log) => minutesOfClock(log.time)),
          )
          const kept = new Map<string, number[]>()
          for (const beside of onDay.get(each) ?? []) {
            /* Usual times are read off the days' earliest times, so that is the time kept. */
            if (onOnePlate(beside.minutes, earliest)) {
              kept.set(beside.thingId, [earliest])
            }
          }
          return { rank, kept }
        }),
        alreadyThere: () => false,
        timed: true,
      })
    }

    /* The habit of the hour: the three hours either side of the time asked for. */
    const inStretch = (at: number): boolean => Math.abs(at - askedMinutes) <= HABIT_STRETCH_MINUTES
    situations.push({
      reading: 'habit',
      occasions: counted.map((each, rank) => {
        const kept = new Map<string, number[]>()
        for (const beside of onDay.get(each) ?? []) {
          if (!inStretch(beside.minutes)) {
            continue
          }
          const times = kept.get(beside.thingId) ?? []
          times.push(beside.minutes)
          kept.set(beside.thingId, times)
        }
        return { rank, kept }
      }),
      alreadyThere: (thingId) =>
        onTheDay.some((each) => each.thingId === thingId && inStretch(each.minutes)),
      timed: true,
    })

    /* The follower: what comes along half an hour to three hours after a thing. */
    const followed = new Map<string, number>()
    for (const each of onTheDay) {
      const since = askedMinutes - each.minutes
      if (since < FOLLOW_FROM_MINUTES || since > FOLLOW_TO_MINUTES) {
        continue
      }
      const before = followed.get(each.thingId)
      if (before === undefined || each.minutes > before) {
        followed.set(each.thingId, each.minutes)
      }
    }
    for (const [base, firstAt] of followed) {
      const days = counted.filter((each) => timesOn(base, each).length > 0)
      situations.push({
        reading: 'follower',
        occasions: days.map((each, rank) => {
          const times = timesOn(base, each)
          const kept = new Map<string, number[]>()
          for (const beside of onDay.get(each) ?? []) {
            const follows = times.some((at) => {
              const since = beside.minutes - at
              return since >= FOLLOW_FROM_MINUTES && since <= FOLLOW_TO_MINUTES
            })
            if (follows) {
              kept.set(beside.thingId, [])
            }
          }
          return { rank, kept }
        }),
        alreadyThere: (thingId) =>
          onTheDay.some((each) => each.thingId === thingId && each.minutes > firstAt),
        timed: false,
      })
    }

    /* After training: read off the workouts the account has, shown on the food log or not. */
    const justFinished = input.workouts
      .filter((workout) => {
        if (countedDayOfMoment(workout.finishedAt) !== day) {
          return false
        }
        const since = askedMinutes - minutesOfMoment(workout.finishedAt)
        return since >= 0 && since <= AFTER_TRAINING_MINUTES
      })
      .reduce<TrainedWorkout | null>(
        (latest, workout) =>
          latest === null || workout.finishedAt > latest.finishedAt ? workout : latest,
        null,
      )
    if (justFinished !== null) {
      const trained = input.workouts
        .filter((workout) => countedDays.has(countedDayOfMoment(workout.finishedAt)))
        .sort((a, b) => a.finishedAt - b.finishedAt)
      const finishedAt = minutesOfMoment(justFinished.finishedAt)
      situations.push({
        reading: 'after-training',
        occasions: trained.map((workout, rank) => {
          const on = countedDayOfMoment(workout.finishedAt)
          const finished = minutesOfMoment(workout.finishedAt)
          const kept = new Map<string, number[]>()
          for (const beside of onDay.get(on) ?? []) {
            const since = beside.minutes - finished
            if (since >= 0 && since <= AFTER_TRAINING_MINUTES) {
              kept.set(beside.thingId, [])
            }
          }
          return { rank, kept }
        }),
        alreadyThere: (thingId) =>
          onTheDay.some((each) => each.thingId === thingId && each.minutes >= finishedAt),
        timed: false,
      })
    }
  }

  const says = new Map<string, Say>()
  const keep = (say: Say | null, thingId: string): void => {
    if (say === null) {
      return
    }
    const before = says.get(thingId)
    if (before === undefined || betterThan(say, before)) {
      says.set(thingId, say)
    }
  }

  for (const situation of situations) {
    const total = situation.occasions.length
    if (total === 0) {
      continue
    }
    const latest = [...situation.occasions]
      .sort((a, b) => b.rank - a.rank)
      .slice(0, MISSED_IN_A_ROW)
    for (const thingId of things) {
      /* What has had its day is not what comes next, whatever the reading would have said. */
      if (spent.has(thingId)) {
        continue
      }
      const on = situation.occasions.filter((occasion) => occasion.kept.has(thingId))
      if (on.length === 0) {
        continue
      }
      /*
       * A reading says nothing of a thing already there for it, nothing of one whose standing
       * time has passed, and nothing of one missed on its two most recent occasions: a habit
       * missed twice running is not a habit today. The thing stays in the list by its worth.
       */
      if (!latest.some((occasion) => occasion.kept.has(thingId))) {
        continue
      }
      if (situation.alreadyThere(thingId)) {
        continue
      }
      if (situation.timed) {
        const times = on.flatMap((occasion) => occasion.kept.get(thingId) ?? [])
        if (!withinUsualTimes(times, askedMinutes)) {
          continue
        }
      }
      keep(sayOf(situation.reading, on.length / total, total), thingId)
    }
  }

  /*
   * The companion, which is not read through a situation like the rest: it is read through as
   * many of them as the plate has foods and then held to the least of what they say.
   */
  const plate = plateOf(input, entriesOnDay.get(day) ?? [])
  if (plate !== null) {
    const onThePlate = new Set(plate.keys.map(foodThing))
    const plateAt = plate.at
    const alreadyThere = (thingId: string): boolean =>
      onThePlate.has(thingId) ||
      (plateAt !== null &&
        onTheDay.some(
          (each) =>
            each.thingId === thingId &&
            Math.abs(each.minutes - plateAt) <= COMPANION_STANDS_MINUTES,
        ))

    /*
     * Every plate a food on this plate has stood on in the window, and what stood on it with
     * it — a food eaten in two dishes a day is read plate by plate rather than as one habit,
     * so the milk that goes in the coffee is not made the milk of every plate of the morning.
     */
    const plates = plate.keys.map((key) => {
      const stood = (byThing.get(foodThing(key)) ?? [])
        .filter((each) => countedDays.has(each.day))
        .sort((a, b) => (a.day === b.day ? a.minutes - b.minutes : a.day < b.day ? -1 : 1))
      const occasions = stood.map((each, rank) => {
        const kept = new Map<string, number[]>()
        for (const beside of onDay.get(each.day) ?? []) {
          if (onOnePlate(beside.minutes, each.minutes)) {
            kept.set(beside.thingId, [])
          }
        }
        return { rank, kept }
      })
      return occasions
    })

    for (const thingId of things) {
      /* The companion speaks of foods; a meal is never a companion. */
      if (!thingId.startsWith('food:') || alreadyThere(thingId) || spent.has(thingId)) {
        continue
      }
      let least: { share: number; occasions: number } | null = null
      let spoken = true
      for (const occasions of plates) {
        /*
         * A food on the plate with no occasion at all has never been eaten beside anything, and
         * says nothing either way: trying something new beside the coffee does not silence the
         * milk.
         */
        if (occasions.length === 0) {
          continue
        }
        /*
         * A plate that has something the thing was never eaten beside is not the plate the
         * thing belongs to, whatever the rest of the plate says and however seldom that
         * something has been logged.
         */
        if (!occasions.some((occasion) => occasion.kept.has(thingId))) {
          spoken = false
          break
        }
        /* A food on the plate with fewer than eight occasions says nothing either way. */
        if (occasions.length < LEAST_OCCASIONS) {
          continue
        }
        const on = occasions.filter((occasion) => occasion.kept.has(thingId))
        const share = rounded(on.length / occasions.length)
        const latest = [...occasions].sort((a, b) => b.rank - a.rank).slice(0, MISSED_IN_A_ROW)
        if (!latest.some((occasion) => occasion.kept.has(thingId))) {
          spoken = false
          break
        }
        /*
         * The least of what the plate says — the plate foods are read in the order they were
         * logged, so on a tie both ports take the occasions of the same one.
         */
        if (least === null || share < least.share) {
          least = { share, occasions: occasions.length }
        }
      }
      if (!spoken || least === null) {
        continue
      }
      keep(sayOf('companion', least.share, least.occasions), thingId)
    }
  }

  /*
   * A food logged from one meal three times running stands as that meal: the person has started
   * logging it that way, and the list says so before the meal's own share has caught up.
   */
  const collapsedTo = (thingId: string): string | null => {
    if (form || !thingId.startsWith('food:')) {
      return null
    }
    const newest = [...(byThing.get(thingId) ?? [])]
      .sort((a, b) => b.loggedAt - a.loggedAt)
      .slice(0, COLLAPSE_LOGS)
    if (newest.length < COLLAPSE_LOGS) {
      return null
    }
    const mealId = newest[0]?.mealId ?? null
    if (mealId === null || !mealById.has(mealId)) {
      return null
    }
    return newest.every((each) => each.mealId === mealId) ? mealThing(mealId) : null
  }

  return { says, spent, collapsedTo }
}

/**
 * The reckonings already made, by what they were made off. A screen places several lists off
 * one asking — the favourites, the foods logged before, each kind of its own — and the log is
 * read for its habits once for all of them rather than once a list.
 */
const reckonings = new WeakMap<object, Reckoning>()

function reckoningOf<E>(input: PlacingInput<E>): Reckoning {
  const held = reckonings.get(input)
  if (held !== undefined) {
    return held
  }
  const reckoning = reckon(input)
  reckonings.set(input, reckoning)
  return reckoning
}

/**
 * One press of a meal as one occurrence of it: at the earliest of the times its entries were
 * logged onto, and at the last of the moments they were logged at, which is what the collapse
 * reads.
 */
function pressed(press: readonly Logged[], mealId: string, day: string): Occurrence {
  return {
    thingId: mealThing(mealId),
    day,
    minutes: Math.min(...press.map((each) => minutesOfClock(each.time))),
    loggedAt: Math.max(...press.map((each) => each.loggedAt)),
    mealId,
  }
}

/**
 * A reading's say: its share, scaled down where it rests on fewer than eight occasions, and
 * nothing at all where what is left is under a quarter.
 */
function sayOf(reading: Reading, raw: number, occasions: number): Say | null {
  const share = rounded(raw)
  const estimate = rounded(share * Math.min(1, occasions / LEAST_OCCASIONS))
  if (estimate < LEAST_SAY) {
    return null
  }
  return { reading, share, occasions, estimate }
}

/** Which of two says of one thing the estimate is. */
function betterThan(say: Say, before: Say): boolean {
  if (say.estimate !== before.estimate) {
    return say.estimate > before.estimate
  }
  if (say.occasions !== before.occasions) {
    return say.occasions > before.occasions
  }
  return READINGS_IN_ORDER.indexOf(say.reading) < READINGS_IN_ORDER.indexOf(before.reading)
}

/** The plate the companion reads: the one just logged, or the one a form holds. */
function plateOf<E>(
  input: PlacingInput<E>,
  onTheDay: readonly Logged[],
): { keys: string[]; at: number | null } | null {
  const { purpose } = input.asking
  if (purpose.kind === 'form') {
    const keys = [...new Set(purpose.plateKeys)]
    return keys.length === 0 ? null : { keys, at: null }
  }
  const last = onTheDay.reduce<Logged | null>(
    (latest, log) => (latest === null || log.loggedAt > latest.loggedAt ? log : latest),
    null,
  )
  if (
    last === null ||
    input.asking.now - last.loggedAt > COMPANION_STANDS_MINUTES * MS_IN_A_MINUTE
  ) {
    return null
  }
  const at = minutesOfClock(last.time)
  const keys = [
    ...new Set(
      onTheDay.flatMap((log) =>
        log.key !== null && onOnePlate(minutesOfClock(log.time), at) ? [log.key] : [],
      ),
    ),
  ]
  return { keys, at }
}

/** What each row is worth on its own, read off the entries and off what the row says of itself. */
function candidateOf(
  standing: Standing,
  part: PartOfTheDay,
  form: boolean,
  entriesByKey: Map<string, Logged[]>,
): Candidate {
  if (standing.kind === 'unkept') {
    /* Something logged without being kept has one time, at the moment it was logged. */
    return {
      moment: standing.loggedAt,
      times: 1,
      readTimes: 1,
      inPart: !form && partOfMoment(standing.loggedAt) === part ? 1 : 0,
      name: standing.name,
    }
  }
  if (standing.kind === 'meal') {
    /* A meal reads its part of the day off the newest five logs it carries, and no further. */
    const logs = [...standing.logs].sort((a, b) => b.loggedAt - a.loggedAt).slice(0, LOGS_READ)
    return {
      moment: standing.lastLoggedAt ?? standing.madeAt,
      times: standing.logCount,
      readTimes: logs.length,
      inPart: form ? 0 : logs.filter((log) => partOfMoment(log.loggedAt) === part).length,
      name: standing.name,
    }
  }
  /* An entry logged from a meal is none of its food's times, the meal standing as itself. */
  const logs = entriesByKey.get(standing.key) ?? []
  const moment = logs.reduce<number | null>(
    (latest, log) => (latest === null || log.loggedAt > latest ? log.loggedAt : latest),
    null,
  )
  return {
    moment: moment ?? standing.madeAt,
    times: logs.length,
    readTimes: logs.length,
    inPart: form ? 0 : logs.filter((log) => partOfTheDayOf(log.time) === part).length,
    name: standing.name,
  }
}

function partOfTheDayOf(time: string): PartOfTheDay {
  return partOfMinutes(minutesOfClock(time))
}

/**
 * A list placed by the one reckoning: what the log expects first, what is familiar after it,
 * and what has hardly been touched last.
 *
 * What it was handed is left alone: the placed list is a new one, and a food row that stands as
 * a meal row of the list is not in it.
 */
export function placed<T, E>(
  rows: readonly T[],
  describe: (row: T) => Standing,
  input: PlacingInput<E>,
): Placed<T>[] {
  const { says, spent, collapsedTo } = reckoningOf(input)
  const { asking } = input
  const form = asking.purpose.kind === 'form'
  const part = partOfMinutes(minutesOfClock(asking.time))

  /* The worth reads the ninety days back from the day asked for, the readings only twenty-eight. */
  const entriesByKey = new Map<string, Logged[]>()
  for (const row of input.entries) {
    const log = input.of(row)
    /* An entry logged from a meal is none of its food's times, whether or not it says which. */
    if (log.key === null || log.mealId !== null || log.fromMeal === true) {
      continue
    }
    const back = daysBetween(asking.day, log.day)
    if (back < 0 || back >= RECENT_DAYS) {
      continue
    }
    const of = entriesByKey.get(log.key) ?? []
    of.push(log)
    entriesByKey.set(log.key, of)
  }

  const held = rows.map((row) => {
    const standing = describe(row)
    const thingId = thingOf(standing)
    const candidate = candidateOf(standing, part, form, entriesByKey)
    return {
      row,
      thingId,
      name: standing.name,
      moment: candidate.moment,
      worth: worthOf(candidate, asking.now),
      spent: spent.has(thingId),
      say: says.get(thingId) ?? null,
      standsAs: null as string | null,
    }
  })

  /*
   * The collapse happens only in a list that offers meals: a list of one kind has no meal to
   * stand the food as, and the food stands as itself. A food whose estimate is the companion's
   * is the other exception — it is the rest of a plate, and stands as the food it is.
   */
  const mealRows = new Map(
    held.filter((each) => each.thingId.startsWith('meal:')).map((each) => [each.thingId, each]),
  )
  for (const each of held) {
    if (!each.thingId.startsWith('food:') || each.say?.reading === 'companion') {
      continue
    }
    const into = collapsedTo(each.thingId)
    if (into === null) {
      continue
    }
    const meal = mealRows.get(into)
    if (meal === undefined) {
      continue
    }
    each.standsAs = into
    if (each.say !== null && (meal.say === null || betterThan(each.say, meal.say))) {
      meal.say = each.say
    }
  }

  return held
    .filter((each) => each.standsAs === null)
    .sort((a, b) => {
      /* What is not spent stands before what is; among the spent, the order holds as it would. */
      if (a.spent !== b.spent) {
        return a.spent ? 1 : -1
      }
      const estimate = (a.say?.estimate ?? 0) - (b.say?.estimate ?? 0)
      if (estimate !== 0) {
        return -estimate
      }
      if (a.worth !== b.worth) {
        return b.worth - a.worth
      }
      if (a.moment !== b.moment) {
        /* One never logged and never written stands after one with a moment, whatever its name. */
        if (a.moment === null) {
          return 1
        }
        if (b.moment === null) {
          return -1
        }
        return b.moment - a.moment
      }
      /*
       * Lowercased and compared character by character, which is the one rule both ports can
       * hold to: a locale-aware comparison and a code-unit one disagree over case and accents.
       */
      const named = a.name.toLowerCase()
      const beside = b.name.toLowerCase()
      return named < beside ? -1 : named > beside ? 1 : 0
    })
    .map((each) => ({
      row: each.row,
      estimate: each.say?.estimate ?? 0,
      reading: each.say?.reading ?? null,
      share: each.say?.share ?? null,
      occasions: each.say?.occasions ?? null,
      worth: each.worth,
      spent: each.spent,
      standsAs: each.standsAs,
    }))
}
